import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CustomizerSettingsService } from '../../../../customizer-settings/customizer-settings.service';
import { LoadingService } from '../../../../services/loading.service';
import { ObjectUtility } from '../../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../../validators/ValidationService';
import { CommonModule } from '@angular/common';
import { State } from '../../../../models/domain/State';
import { StateService } from '../../../../services/state.service';
import { Country } from '../../../../models/domain/Country';
import { BaseSearchOptions } from '../../../../models/core/table/BaseSearchOptions';
import { CountryService } from '../../../../services/country.service';

@Component({
  selector: 'app-state-details',
  templateUrl: './state-details.component.html',
  styleUrl: './state-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule],
})

export class StateDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() data!: State;
  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public stateOriginal = new State;
  public state: State = new State;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  public currentStateId = 0;
  isSubmitted = false;

  //dropdown DataBind
  public country$!: Observable<Country[]>;

  public country!: Country[];
  public pageIndex = 0;
  public pageSize = 1000;
  public searchOptions!: BaseSearchOptions;

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private stateService: StateService,
    private toasterService: ToastrService,
    private loadingService: LoadingService,
    private countryService: CountryService,

  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    this.getDropdownData();
    if (this.data) {
      this.currentStateId = this.data.id ? this.data.id : 0;
      this.state = ObjectUtility.deepClone(this.data);
      this.stateOriginal = ObjectUtility.deepClone(this.data);
      this.setForm(this.state);
    }
    else {
      this.currentStateId = 0;
      const newState = new State();
      this.state = ObjectUtility.deepClone(newState);
      this.stateOriginal = ObjectUtility.deepClone(newState);
      this.setForm(this.state);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });

    this.searchOptions = new BaseSearchOptions(undefined, 'name', 0, this.pageIndex, this.pageSize);
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {

    this.frmGroup.controls['countryNameControl'].valueChanges.subscribe(value => {
      this.state.country = value;
    });

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.state.name = value;
    });

     this.frmGroup.controls['codeControl'].valueChanges.subscribe(value => {
      this.state.code = value;
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get f() { return this.frmGroup.controls; }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      nameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      countryNameControl: new FormControl(null, [Validators.required]),
     codeControl: new FormControl('', whitespaceValidator()),
    })
  }

  private setForm(state: State): void {
    if (state) {
      this.frmGroup.patchValue({
        nameControl: state.name,
        codeControl: state.code,
        countryNameControl: state?.country,

      });
    }
    this.titleText = `${this.currentStateId === 0 ? "Add" : "Edit"} State`;
    this.titleButton = `${this.currentStateId === 0 ? "Create" : "Update"}`;
  }

  public onSave(): void {
    this.isSubmitted = true;
    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditState();
    }
  }

  private addEditState(): void {
    if (this.state?.id === 0) {
      this.stateService.create(this.sanitizeService(this.state))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.state = response?.body != null ? response?.body : new State;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.stateService.update(this.sanitizeService(this.state))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.state = response?.body != null ? response?.body : new State;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }


  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.state, this.stateOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['countryNameControl'].valid && this.frmGroup.controls['nameControl'].valid
    return isValid;
  }

  public countryNameComparer(t1: Country, t2: Country): boolean {
    return t1 && t2 ? (t1.id === t2.id || t1.name === t2.name) : t1 === t2;
  }

  private sanitizeService(state: State): State {
    state.country = null;
    state.countryId = this.frmGroup.controls['countryNameControl'].value.id;

    return state;
  }

  private getDropdownData() {
    this.loadingService.showLoading();

    this.country$ = this.countryService.get(this.searchOptions).pipe(
      map((result: any) => {
        this.loadingService.hideLoading();
        return result.body.values
      }),
      catchError(() => {
        this.loadingService.hideLoading();
        return of([])
      })
    );

    /*const country = this.countryService.get(this.searchOptions);
    forkJoin([country])
      .pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        if (result) {
          this.country = result[0].body.values;
          this.setForm(this.state);
          this.loadingService.hideLoading();
        }
      });*/
  }

}